import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Loader2, Plus, Trash2, ExternalLink, Users, X, CheckCircle2, AlertTriangle, Info, ClipboardList, BookMarked,
} from "lucide-react";

const FRAMEWORKS = ["NIS2", "DORA", "GDPR", "OT"];
const FW_DESC = {
  NIS2: "Direttiva UE su cybersecurity di soggetti essenziali e importanti.",
  DORA: "Regolamento UE sulla resilienza operativa digitale del settore finanziario.",
  GDPR: "Regolamento UE sulla protezione dei dati personali.",
  OT: "Sicurezza degli ambienti Operational Technology / ICS (impianti, produzione).",
};
const priorityBadge = { ALTA: "bg-destructive/10 text-destructive", MEDIA: "bg-[#F59E0B]/10 text-[#B45309]", BASSA: "bg-[#00A859]/10 text-[#00784A]" };
const ANSWER_OPTS = [
  { v: "yes", label: "Sì", cls: "bg-[#00A859] text-white border-[#00A859]" },
  { v: "partial", label: "Parziale", cls: "bg-[#F59E0B] text-white border-[#F59E0B]" },
  { v: "no", label: "No", cls: "bg-destructive text-white border-destructive" },
];

const scoreColor = (s) => (s >= 80 ? "#00A859" : s >= 50 ? "#F59E0B" : "#DC2626");
const emptyControl = { framework: "NIS2", area: "", question: "", priority: "MEDIA", effort: "MEDIO", recommendation: "", recommended_tools: [{ name: "", note: "", url: "" }] };

function ScoreBar({ label, data }) {
  const color = scoreColor(data.score);
  return (
    <div className="bg-white border border-border p-4" data-testid={`score-${label}`}>
      <div className="flex items-center justify-between">
        <span className="font-heading font-black tracking-tight">{label}</span>
        <span className="font-mono text-sm" style={{ color }}>{data.score}%</span>
      </div>
      <div className="h-2 bg-secondary mt-2 overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${data.score}%`, backgroundColor: color }} />
      </div>
      <div className="data-label mt-2 flex justify-between">
        <span style={{ color }}>{data.level}</span>
        <span>{data.answered}/{data.total} risposte</span>
      </div>
    </div>
  );
}

function RegulationInfo({ control }) {
  if (!control.regulation_ref && !control.regulation_why) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`reg-info-${control.id}`} className="text-primary/70 hover:text-primary shrink-0" title="Riferimento normativo">
          <Info className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="rounded-none w-80" align="start">
        <div className="flex items-center gap-1.5 data-label text-primary mb-1"><BookMarked className="w-3.5 h-3.5" /> {control.regulation_ref || "Riferimento normativo"}</div>
        <p className="text-sm text-muted-foreground">{control.regulation_why}</p>
      </PopoverContent>
    </Popover>
  );
}

const PRIO_ORDER = { ALTA: 0, MEDIA: 1, BASSA: 2 };

function PlanningTable({ controls, answers }) {
  const rows = controls
    .filter((c) => ["no", "partial"].includes(answers[c.id]))
    .map((c) => ({ ...c, status: answers[c.id] === "no" ? "Assente" : "Parziale" }))
    .sort((a, b) => (PRIO_ORDER[a.priority] ?? 1) - (PRIO_ORDER[b.priority] ?? 1));
  if (rows.length === 0) return null;
  return (
    <div className="mt-8 bg-white border border-border" data-testid="planning-table">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-bold tracking-tight">Tabella di Pianificazione</h3>
        <span className="data-label ml-auto">{rows.length} interventi</span>
      </div>
      <p className="text-xs text-muted-foreground px-4 pt-3">Piano d'azione prioritizzato per colmare i gap di conformità rilevati. Incluso nel Report PDF cliente.</p>
      <div className="overflow-x-auto p-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="data-label py-2 pr-3">Issue rilevata</th>
              <th className="data-label py-2 px-3">Priorità</th>
              <th className="data-label py-2 px-3">Improvement consigliato</th>
              <th className="data-label py-2 pl-3">Riferimento normativo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} data-testid={`planning-row-${i}`} className={`border-b border-border align-top ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                <td className="py-2 pr-3">
                  <span className="text-[10px] font-mono bg-primary/10 text-primary px-1.5 py-0.5 mr-1">{r.framework}</span>
                  {r.question}
                  <span className="text-xs text-muted-foreground"> ({r.status})</span>
                </td>
                <td className="py-2 px-3"><span className={`px-2 py-0.5 text-[10px] font-bold ${priorityBadge[r.priority] || "bg-secondary"}`}>{r.priority}</span></td>
                <td className="py-2 px-3 text-muted-foreground">
                  {r.recommendation}
                  {r.recommended_tools?.length > 0 && <div className="text-xs mt-1">Tool: {r.recommended_tools.map((t) => t.name).join(", ")}</div>}
                </td>
                <td className="py-2 pl-3 text-xs text-primary/80">{r.regulation_ref || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ToolChips({ tools }) {
  if (!tools?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {tools.map((t, j) => {
        const isOpswat = /opswat/i.test(t.name || "");
        const chip = (
          <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 ${isOpswat ? "bg-primary/10 text-primary" : "bg-secondary"}`} title={t.note}>
            {t.name}{t.url ? <ExternalLink className="w-2.5 h-2.5" /> : null}
          </span>
        );
        return t.url ? <a key={j} href={t.url} target="_blank" rel="noopener noreferrer">{chip}</a> : <span key={j}>{chip}</span>;
      })}
    </div>
  );
}

export default function ComplianceView() {
  const { activeClient } = useClient();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "member";
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeFw, setActiveFw] = useState("NIS2");
  const [genLoading, setGenLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(emptyControl);

  const clientId = activeClient?.id;

  const load = useCallback(async () => {
    if (!clientId) { setData(null); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get(`/compliance/assessment/${clientId}`);
      setData(d);
      setAnswers(d.answers || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento");
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const controls = (data?.controls || []).filter((c) => c.framework === activeFw);

  const computeScore = (fw) => {
    const list = (data?.controls || []).filter((c) => c.framework === fw);
    if (!list.length) return { score: 0, total: 0, answered: 0, level: "n/d" };
    let pts = 0, answered = 0;
    list.forEach((c) => {
      const a = answers[c.id];
      if (a) answered++;
      if (a === "yes") pts += 1; else if (a === "partial") pts += 0.5;
    });
    const score = Math.round((pts / list.length) * 100);
    const level = score >= 80 ? "Buono" : score >= 50 ? "Parziale" : "Critico";
    return { score, total: list.length, answered, level };
  };

  const setAnswer = async (controlId, val) => {
    if (!canEdit) return;
    const next = { ...answers, [controlId]: val };
    setAnswers(next);
    try {
      await api.put(`/compliance/assessment/${clientId}`, { answers: next });
    } catch (e) {
      toast.error("Salvataggio non riuscito");
    }
  };

  const generateAI = async () => {
    setGenLoading(true);
    try {
      const { data: d } = await api.post(`/compliance/assessment/${clientId}/generate`, { framework: activeFw });
      toast.success(`${d.created} nuovi controlli generati`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generazione non riuscita");
    } finally { setGenLoading(false); }
  };

  const saveControl = async () => {
    if (!form.question.trim()) { toast.error("Domanda obbligatoria"); return; }
    const payload = { ...form, framework: activeFw, recommended_tools: form.recommended_tools.filter((t) => t.name.trim()) };
    try {
      await api.post("/compliance/controls", payload);
      toast.success("Controllo aggiunto"); setDlgOpen(false); setForm(emptyControl); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteControl = async (cid) => {
    if (!window.confirm("Eliminare questo controllo?")) return;
    try { await api.delete(`/compliance/controls/${cid}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updTool = (i, patch) => setForm((f) => ({ ...f, recommended_tools: f.recommended_tools.map((t, idx) => idx === i ? { ...t, ...patch } : t) }));

  if (!clientId) {
    return (
      <div className="bg-white border border-dashed border-border p-12 text-center" data-testid="compliance-no-client">
        <Users className="w-10 h-10 mx-auto text-muted-foreground/50" />
        <p className="mt-3 text-muted-foreground">Seleziona un cliente dal selettore in alto per avviare il self-assessment di conformità.</p>
      </div>
    );
  }

  return (
    <div data-testid="compliance-view">
      <div className="bg-primary/5 border border-primary/20 p-4 flex items-start gap-3 mb-6">
        <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          Self-assessment di cybersecurity e conformità per <span className="font-medium text-foreground">{activeClient.name}</span>. Rispondi ai controlli per calcolare il livello di conformità a NIS2, DORA e GDPR e ottenere le remediation consigliate.
        </p>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {FRAMEWORKS.map((fw) => <ScoreBar key={fw} label={fw} data={computeScore(fw)} />)}
      </div>

      {/* Framework tabs */}
      <div className="flex items-center justify-between gap-4 border-b border-border mb-4 flex-wrap">
        <div className="flex gap-1">
          {FRAMEWORKS.map((fw) => (
            <button key={fw} data-testid={`fw-tab-${fw}`} onClick={() => setActiveFw(fw)}
              className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors duration-150 ${activeFw === fw ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {fw}
            </button>
          ))}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 pb-2">
            <Button data-testid="add-control-btn" variant="outline" size="sm" className="rounded-none" onClick={() => { setForm({ ...emptyControl, framework: activeFw }); setDlgOpen(true); }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Controllo
            </Button>
            <Button data-testid="generate-ai-btn" size="sm" className="rounded-none" onClick={generateAI} disabled={genLoading}>
              {genLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Genera con AI
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-4">{FW_DESC[activeFw]}</p>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-3" data-testid="controls-list">
          {controls.map((c, i) => {
            const ans = answers[c.id];
            const showRemediation = ans === "no" || ans === "partial";
            return (
              <div key={c.id} data-testid={`control-${i}`} className="bg-white border border-border p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {c.area && <span className="data-label">{c.area}</span>}
                      <span className={`px-2 py-0.5 text-[10px] font-bold ${priorityBadge[c.priority] || "bg-secondary"}`}>{c.priority}</span>
                      {c.source === "ai" && <span className="text-[10px] font-mono bg-primary/10 text-primary px-2 py-0.5">AI</span>}
                    </div>
                    <p className="text-sm mt-2">{c.question}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <RegulationInfo control={c} />
                    {c.editable && (
                      <button onClick={() => deleteControl(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-control-${i}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>

                <div className="flex gap-1 mt-3">
                  {ANSWER_OPTS.map((o) => (
                    <button key={o.v} data-testid={`answer-${i}-${o.v}`} disabled={!canEdit} onClick={() => setAnswer(c.id, o.v)}
                      className={`px-3 py-1 text-xs font-medium border transition-colors duration-150 ${ans === o.v ? o.cls : "bg-white text-muted-foreground border-border hover:border-foreground"} ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}>
                      {o.label}
                    </button>
                  ))}
                </div>

                {showRemediation && (
                  <div className="mt-3 pt-3 border-t border-border bg-secondary/30 -mx-4 -mb-4 px-4 py-3" data-testid={`remediation-${i}`}>
                    <div className="flex items-center gap-1.5 data-label text-[#B45309]"><AlertTriangle className="w-3.5 h-3.5" /> Remediation consigliata</div>
                    <p className="text-sm text-muted-foreground mt-1">{c.recommendation}</p>
                    {c.regulation_ref && <p className="text-xs text-primary/80 mt-1 flex items-center gap-1"><BookMarked className="w-3 h-3" /> {c.regulation_ref}</p>}
                    <ToolChips tools={c.recommended_tools} />
                  </div>
                )}
                {ans === "yes" && (
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-[#00784A]"><CheckCircle2 className="w-3.5 h-3.5" /> Conforme</div>
                )}
              </div>
            );
          })}
          {controls.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Nessun controllo per questo framework.</p>}
        </div>
      )}

      {/* Tabella di Pianificazione */}
      <PlanningTable controls={data?.controls || []} answers={answers} />

      {/* Add custom control dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Nuovo controllo — {activeFw}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="data-label">Domanda di controllo *</Label><Textarea data-testid="control-question-input" className="mt-1 rounded-none" rows={2} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="data-label">Area</Label><Input className="mt-1 rounded-none" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></div>
              <div>
                <Label className="data-label">Priorità</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{["ALTA", "MEDIA", "BASSA"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="data-label">Effort</Label>
                <Select value={form.effort} onValueChange={(v) => setForm({ ...form, effort: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{["BASSO", "MEDIO", "ALTO"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="data-label">Raccomandazione</Label><Textarea className="mt-1 rounded-none" rows={2} value={form.recommendation} onChange={(e) => setForm({ ...form, recommendation: e.target.value })} /></div>
            <div>
              <Label className="data-label">Tool consigliati</Label>
              <div className="space-y-2 mt-1">
                {form.recommended_tools.map((t, i) => (
                  <div key={i} className="flex gap-2">
                    <Input className="rounded-none h-8 text-sm" value={t.name} onChange={(e) => updTool(i, { name: e.target.value })} placeholder="Nome tool" />
                    <Input className="rounded-none h-8 text-sm" value={t.url} onChange={(e) => updTool(i, { url: e.target.value })} placeholder="URL (opz.)" />
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => setForm((f) => ({ ...f, recommended_tools: [...f.recommended_tools, { name: "", note: "", url: "" }] }))}><Plus className="w-3.5 h-3.5 mr-1" /> Tool</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDlgOpen(false)}>Annulla</Button>
            <Button data-testid="save-control-btn" className="rounded-none" onClick={saveControl}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
